import type { PublicAvailabilitySlot } from "@emr/contracts";
import { formatTime, slotDurationMinutes } from "../lib/appointmentTime";
import { type DayPeriod, dayPeriodLabels, dayPeriodOf } from "./availabilityCalendar";

export function sameSlot(
  left: PublicAvailabilitySlot | null | undefined,
  right: PublicAvailabilitySlot,
): boolean {
  return (
    left?.startAt === right.startAt &&
    left.endAt === right.endAt &&
    left.providerId === right.providerId
  );
}

/** Times in order, then by who the patient would see. */
export function sortSlots(
  slots: readonly PublicAvailabilitySlot[],
  physicianName: (providerId: string) => string,
): PublicAvailabilitySlot[] {
  const name = (providerId: string | undefined) =>
    providerId === undefined ? "" : physicianName(providerId);
  return [...slots].sort(
    (left, right) =>
      Date.parse(left.startAt) - Date.parse(right.startAt) ||
      Date.parse(left.endAt) - Date.parse(right.endAt) ||
      name(left.providerId).localeCompare(name(right.providerId)),
  );
}

/** The physicians a day's times name, once each, in order. */
export function namedPhysicians(slots: readonly PublicAvailabilitySlot[]): string[] {
  return [
    ...new Set(slots.flatMap((slot) => (slot.providerId === undefined ? [] : [slot.providerId]))),
  ];
}

/**
 * One day's times as small chips, grouped Morning, Afternoon and Evening on the clinic's clock
 * (PA-9: booking and a time change share them). Each chip shows its start time; its radio's name
 * also says the end, who it is with and how long, so a screen reader hears the whole time. A
 * day whose times name more than one physician shows the name on each chip; a clinic-assigned
 * time names none (the clinic assigns one).
 */
export function SlotChoices({
  slots,
  timeZone,
  selected,
  select,
  physicianName,
  name,
}: {
  /** The day's times, sorted (sortSlots). */
  readonly slots: readonly PublicAvailabilitySlot[];
  readonly timeZone: string;
  readonly selected: PublicAvailabilitySlot | null;
  readonly select: (slot: PublicAvailabilitySlot) => void;
  /** Who a time is with, by its physician handle. */
  readonly physicianName: (providerId: string) => string;
  /** The radios' group name. */
  readonly name: string;
}) {
  const showNames = namedPhysicians(slots).length > 1;
  const periods = new Map<DayPeriod, PublicAvailabilitySlot[]>();
  for (const slot of slots) {
    const period = dayPeriodOf(slot.startAt, timeZone);
    periods.set(period, [...(periods.get(period) ?? []), slot]);
  }
  // One radio group (the "Select time" fieldset around this); the periods are visual headings,
  // since every chip's name already says a.m. or p.m.
  return (
    <div className="booking-slot-grid pa-time-groups">
      {(["morning", "afternoon", "evening"] as const).map((period) => {
        const times = periods.get(period);
        if (times === undefined) return null;
        return (
          <div key={period} className="pa-time-group">
            <p className="pa-time-period">{dayPeriodLabels[period]}</p>
            <div className={showNames ? "pa-time-chips with-names" : "pa-time-chips"}>
              {times.map((slot) => {
                const checked = sameSlot(selected, slot);
                return (
                  <label
                    key={`${slot.providerId ?? "clinic"}-${slot.startAt}-${slot.endAt}`}
                    className={checked ? "pa-time selected" : "pa-time"}
                  >
                    <input
                      type="radio"
                      name={name}
                      required
                      checked={checked}
                      onChange={() => select(slot)}
                    />
                    {/* The spaces sit outside the hidden parts so every name computation
                        keeps them: "9:00 a.m. to 9:30 a.m., Dr. Maya Chen, 30 minutes". */}
                    <span className="pa-time-start">
                      {formatTime(slot.startAt, timeZone)}{" "}
                      <span className="sr-only">to {formatTime(slot.endAt, timeZone)},</span>
                    </span>{" "}
                    {/* Without a physician the clinic assigns one when the patient books. */}
                    {slot.providerId === undefined ? null : (
                      <>
                        <span className={showNames ? "pa-time-who" : "sr-only"}>
                          {physicianName(slot.providerId)}
                        </span>
                        <span className="sr-only">,</span>{" "}
                      </>
                    )}
                    <span className="sr-only">
                      {slotDurationMinutes(slot.startAt, slot.endAt)} minutes
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
